import React from 'react';
import { CheckCircle2, ChevronDown, CalendarDays, Download, ArrowRight, HeartHandshake, Check } from 'lucide-react';

const Field = ({ label, ph, icon: Icon, wide }) => (
  <div className={wide ? 'col-span-2' : ''}>
    <p className="text-[10px] text-slate-500 mb-1">{label}</p>
    <div className="h-7 rounded-md border border-slate-200 bg-white px-2 flex items-center justify-between text-[10px] text-slate-400">
      {ph}{Icon && <Icon className="w-3 h-3 text-slate-500" />}
    </div>
  </div>
);

export const MockCard = ({ title, children, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white p-3.5 shadow-[0_6px_20px_-12px_rgba(11,59,60,0.35)] ${className}`}>
    {title && <p className="text-[12px] font-bold text-[#0B2E2F] mb-2.5">{title}</p>}
    {children}
  </div>
);

const Sig = ({ label = 'Signature *', name }) => (
  <div>
    <p className="text-[10px] text-slate-500 mb-1">{label}</p>
    <div className="relative h-16 rounded-md border border-dashed border-slate-300 bg-slate-50/50 flex items-center justify-center">
      <svg viewBox="0 0 160 50" className="w-32 h-10 text-[#0B2E2F]"><path d="M8 38 C 20 10, 30 8, 34 30 S 50 46, 62 20 S 80 6, 88 28 S 120 34, 152 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
      <span className="absolute right-2 bottom-1 text-[9px] text-teal-700">Clear</span>
    </div>
    <div className="grid grid-cols-2 gap-2 mt-2"><Field label="Name" ph={name} /><Field label="Date" ph="dd/mm/yyyy" icon={CalendarDays} /></div>
  </div>
);

export const MockOpen = () => (
  <MockCard>
    <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[10px] text-slate-500 mb-3">
      <span className="w-2 h-2 rounded-full bg-slate-300" />staymii.com/care/register/
    </div>
    <div className="flex items-center gap-2 mb-3">
      <span className="w-8 h-8 rounded-lg bg-teal-600 grid place-items-center"><HeartHandshake className="w-4 h-4 text-white" /></span>
      <div><p className="text-[13px] font-bold text-[#0B2E2F] leading-none">StayMii Care</p><p className="text-[9px] text-slate-500 mt-0.5">Care Management System</p></div>
    </div>
    <div className="rounded-lg bg-slate-50 p-3">
      <p className="text-[12px] font-bold text-[#0B2E2F]">Resident Registration</p>
      <p className="text-[10px] text-slate-500 mt-1">Please complete the following steps to register a resident.</p>
      <div className="mt-3 h-8 rounded-md bg-teal-700 text-white text-[11px] font-semibold flex items-center justify-center gap-1.5">Start Registration <ArrowRight className="w-3 h-3" /></div>
    </div>
  </MockCard>
);

export const MockResident = () => (
  <MockCard title="Resident's Details">
    <div className="grid grid-cols-2 gap-2">
      <Field label="Full Name (as in NRIC/Passport)" ph="e.g. Tan Ah Kow" /><Field label="NRIC / Passport No." ph="e.g. 460101-07-1234" />
      <Field label="Date of Birth" ph="dd/mm/yyyy" icon={CalendarDays} /><Field label="Gender" ph="Select" icon={ChevronDown} />
      <Field label="Contact Number (if any)" ph="e.g. 012-345 6789" /><Field label="Email (if any)" ph="e.g. name@email.com" />
      <Field label="Home Address" ph="e.g. 12, Jalan Bunga, 10050 George Town, Penang" wide />
    </div>
  </MockCard>
);

export const MockRep = () => (
  <MockCard title="Admission Details">
    <div className="w-1/2 pr-1"><Field label="Expected Admission Date" ph="dd/mm/yyyy" icon={CalendarDays} /></div>
    <p className="text-[11px] font-bold text-[#0B2E2F] mt-3 mb-2">Primary Representative</p>
    <div className="grid grid-cols-3 gap-2">
      <Field label="Name" ph="e.g. Tan Mei Ling" /><Field label="Relationship" ph="Daughter" /><Field label="I/C No." ph="790815-07-5684" />
    </div>
    <div className="grid grid-cols-3 gap-2 mt-2"><Field label="Contact Number" ph="012-345 6789" /><div className="col-span-2"><Field label="Email (for invoices & receipts)" ph="name@email.com" /></div></div>
  </MockCard>
);

export const MockConfirm = () => (
  <MockCard title="Confirmation">
    <div className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5">
      <span className="mt-0.5 w-4 h-4 shrink-0 rounded bg-teal-600 grid place-items-center"><Check className="w-3 h-3 text-white" /></span>
      <p className="text-[10px] text-slate-600">I confirm the information is true and accurate, and I agree to be contacted by the care home about this registration.</p>
    </div>
    <div className="mt-3 h-9 rounded-md bg-teal-700 text-white text-[12px] font-semibold flex items-center justify-center gap-1.5">Submit Registration <ArrowRight className="w-3.5 h-3.5" /></div>
  </MockCard>
);

export const MockAdmission = () => (
  <MockCard title="Resident Admission Form"><Sig label="Resident / Representative Signature *" name="e.g. Tan Ah Kow" /></MockCard>
);

export const MockPdpa = () => (
  <MockCard>
    <div className="flex gap-1.5 mb-3">
      <span className="px-2.5 py-1 rounded-md bg-teal-50 text-teal-800 text-[10px] font-semibold border border-teal-200">1. PDPA Consent</span>
      <span className="px-2.5 py-1 rounded-md bg-slate-50 text-slate-500 text-[10px] border border-slate-200">2. Resident Contract</span>
    </div>
    <p className="text-[12px] font-bold text-[#0B2E2F] mb-2">PDPA Notice & Consent</p>
    <Sig name="e.g. Tan Ah Kow" />
  </MockCard>
);

const CHECK = [['Resident\'s I/C or passport', 1], ['Medical report / discharge summary', 1], ['Next of kin contact details', 1],
  ['List of current medications', 0], ['Deposit / admission fee payment', 0], ['Clothing labelled with resident\'s name', 0]];

export const MockChecklist = () => (
  <MockCard title="Resident Admission Checklist">
    <ul className="space-y-1.5">
      {CHECK.map(([t, on]) => (
        <li key={t} className="flex items-center gap-2 text-[10.5px] text-slate-700">
          <span className={`w-3.5 h-3.5 rounded grid place-items-center ${on ? 'bg-teal-600' : 'border border-slate-300 bg-white'}`}>{on ? <Check className="w-2.5 h-2.5 text-white" /> : null}</span>{t}
        </li>
      ))}
    </ul>
  </MockCard>
);

export const MockDone = () => (
  <MockCard className="text-center">
    <CheckCircle2 className="w-9 h-9 text-teal-600 mx-auto" />
    <p className="text-[13px] font-bold text-[#0B2E2F] mt-2">All documents signed!</p>
    <p className="text-[10px] text-slate-500 mt-1">Thank you. Your documents have been sent to the care home.</p>
    <div className="mt-3 h-9 rounded-md bg-teal-700 text-white text-[11px] font-semibold flex items-center justify-center gap-1.5"><Download className="w-3.5 h-3.5" />Download Signed Documents (PDF)</div>
  </MockCard>
);
